export interface CurrentUser {
  id: string;
  email: string;
  fullName: string;
  role: string;
}

export interface LoginResponse {
  token: string;
  refreshToken?: string;
  expiresIn?: number;
}

export interface DataTablesResponse<T> {
  draw: number;
  recordsTotal: number;
  recordsFiltered: number;
  data: T[];
}

export interface IAccountModel {
  id?: number;
  accountResponse: {
    id?: number;
    accountName: string;
    accountStatus: string;
    apiKey: string;
    allowDebugOutput: boolean;
    rateLimitApplies: boolean;
    rateLimitPerMin: number;
    rateLimitPerDay: number;
    whiteListedIPs: string;
    loggingStatus: string;
    primaryServer: { id: number; name: string; status?: string };
    secondaryServer: { id: number; name: string; status?: string };
  };
  licenseResponse: {
    id?: number;
    licenseName: string;
  };
  validityTimeFrame: string;
  isLicenseBlocked: boolean;
}

export interface IServerModel {
  id?: number;
  name: string;
  port: number;
  url: string;
  apiKey: string;
  status: string;
  forceOffline: boolean;
}

export interface ILicenseModel {
  id?: number;
  licenseName: string;
  servers: IServerModel[];
}

export interface IDocumentTypeModel {
  id?: number;
  name: string;
}

export interface INationalityModel {
  id?: number;
  name: string;
}

export interface IKYCFieldModel {
  id?: number;
  name: string;
}

export interface IUserModel {
  id?: number;
  name: string;
  email: string;
  role: string;
  isExternalUser: boolean;
  password?: string;
}

export interface IS3StorageModel {
  id?: number;
  serviceUrl: string;
  bucketName: string;
  accessKey: string;
  secretKey: string;
  enabled: boolean;
  enableIncomingApiCallsLogging: boolean;
  enableOutgoingApiCallsLogging: boolean;
  useUtcTimestamps: boolean;
  maxLogRetentionDays: number;
  maxBodyLogSizeInBytes: number;
  slowIncomingRequestThresholdMs: number;
  slowOutgoingRequestThresholdMs: number;
}

export interface ISeqServerModel {
  id?: number;
  url: string;
  apiKey: string;
  enabled: boolean;
}

export interface ILogServerModel {
  id?: number;
  url: string;
  apiKey: string;
}

export interface IFieldMappingModel {
  id?: number;
  nationalityResponse: { id: number; name: string };
  documentTypeResponse: { id: number; name: string };
  documentSide: string;
  mappingFields: Array<{
    fieldName?: string;
    standardFieldName: string;
    regex?: string;
    kycMappingField: Array<{
      prefix?: string;
      kycField: { id: number; name: string };
      suffix?: string;
    }>;
  }>;
}

export interface IDefaultMappingModel {
  id?: number;
  standardFieldName: string;
  kycFields: { id: number; name: string };
  priority: number;
}

export interface ICostCenterModel {
  id?: number;
  name: string;
  description?: string;
  unpaidCreditLimit?: number;
  isCreditsBlocked: boolean;
}

export interface ICreditBalance {
  availableCredits: number;
  reservedCredits: number;
  isBlocked: boolean;
}

export interface IPricingModel {
  id?: number;
  costCenterId: number;
  operationType: number;
  creditsPerOperation: number;
}

export interface IStatisticsModel {
  accountName: string;
  api: string;
  lastAccess: string;
  lastDaySuccessfulAccess: number;
  lastDayWrongAccess: number;
  lastWeekSuccessfulAccess: number;
  lastWeekWrongAccess: number;
  lastMonthSuccessfulAccess: number;
  lastMonthWrongAccess: number;
}

/* ── OCR Regression Testing ── */

export enum KycOperationType {
  KycSingleSide = 1,
  KycDualSide = 2,
  MrzSingleSide = 3,
  MrzDualSide = 4,
  QrMidni = 5,
  FaceCompare = 6,
}

export enum DocumentSide {
  Front = 1,
  Back = 2,
}

export enum FieldMatchMode {
  Exact = 1,
  Contains = 2,
  Regex = 3,
  NotEmpty = 4,
}

export enum OcrTestResultStatus {
  Passed = 1,
  Failed = 2,
  Error = 3,
}

export interface IOcrTestCase {
  id: string;
  name: string;
  description?: string;
  documentTypeName?: string;
  nationalityCode?: string;
  operationType: KycOperationType;
  isValidated: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
  images?: IOcrTestImage[];
  expectedFields?: IOcrTestExpectedField[];
}

export interface IOcrTestImage {
  id: string;
  testCaseId: string;
  side: DocumentSide;
  contentType: string;
  fileName: string;
  createdAt: string;
}

export interface IOcrTestExpectedField {
  id?: string;
  fieldName: string;
  expectedValue: string;
  matchMode: FieldMatchMode;
}

export interface IOcrTestRun {
  id: string;
  startedAt: string;
  finishedAt?: string;
  sdkVersion?: string;
  notes?: string;
  totalCases: number;
  passedCases: number;
  failedCases: number;
  errorCases: number;
  createdAt: string;
}

export interface IOcrTestRunResult {
  id: string;
  testRunId: string;
  testCaseId: string;
  testCaseName: string;
  status: OcrTestResultStatus;
  responseTimeMs: number;
  errorMessage?: string;
  fieldResults?: IOcrTestFieldResult[];
}

export interface IOcrTestFieldResult {
  id: string;
  fieldName: string;
  expectedValue: string;
  actualValue?: string;
  matchMode: FieldMatchMode;
  matched: boolean;
}
